/**
 * 🛡️ レート制限システム - セキュリティ強化
 * ブルートフォース攻撃対策・IPベース制限・NFCログイン統合
 * 
 * セキュリティ設計:
 * - 5回試行でロックアウト（15分間）
 * - IPアドレス + メソッド別の制限
 * - LocalStorage + SessionStorage併用
 * - セキュア情報漏洩防止
 */

// 🔧 セキュリティ設定
const SECURITY_CONFIG = {
  // ログイン試行制限
  MAX_LOGIN_ATTEMPTS: 5,
  LOCKOUT_DURATION: 15 * 60 * 1000, // 15分
  
  // NFCログイン制限
  MAX_NFC_ATTEMPTS: 3,
  NFC_LOCKOUT_DURATION: 10 * 60 * 1000, // 10分
  
  // 一般的なレート制限
  MAX_REQUESTS_PER_MINUTE: 20,
  
  // クリーンアップ間隔
  CLEANUP_INTERVAL: 60 * 60 * 1000, // 1時間
} as const;

// 📊 試行記録の型定義
export interface AttemptRecord {
  count: number;
  firstAttempt: number;
  lastAttempt: number;
  lockedUntil?: number;
  ipAddress?: string;
  method: 'login' | 'nfc' | 'general';
  userAgent?: string;
}

// 🗃️ ストレージキー
const STORAGE_KEYS = {
  LOGIN_ATTEMPTS: 'auth_login_attempts',
  NFC_ATTEMPTS: 'auth_nfc_attempts',
  GENERAL_REQUESTS: 'auth_general_requests',
  IP_ATTEMPTS: 'auth_ip_attempts',
} as const;

class RateLimiter {
  private cleanupTimer: NodeJS.Timeout | null = null;

  constructor() {
    this.startCleanupTimer();
  }

  /**
   * 🛡️ ログイン試行の制限チェック
   */
  checkLoginAttempt(identifier: string): {
    allowed: boolean;
    remainingAttempts?: number;
    lockoutUntil?: number;
    reason?: string;
  } {
    return this.checkAttempt(
      identifier,
      'login',
      SECURITY_CONFIG.MAX_LOGIN_ATTEMPTS,
      SECURITY_CONFIG.LOCKOUT_DURATION
    );
  }

  /**
   * 🏷️ NFCログイン試行の制限チェック
   */
  checkNFCAttempt(nfcUid: string): {
    allowed: boolean;
    remainingAttempts?: number;
    lockoutUntil?: number;
    reason?: string;
  } {
    return this.checkAttempt(
      nfcUid,
      'nfc',
      SECURITY_CONFIG.MAX_NFC_ATTEMPTS,
      SECURITY_CONFIG.NFC_LOCKOUT_DURATION
    );
  }

  /**
   * 📈 一般リクエストのレート制限
   */
  checkGeneralRequest(identifier: string = 'global'): {
    allowed: boolean;
    remainingRequests?: number;
    resetTime?: number;
  } {
    const result = this.checkAttempt(
      identifier,
      'general',
      SECURITY_CONFIG.MAX_REQUESTS_PER_MINUTE,
      60 * 1000 // 1分間のウィンドウ
    );

    return {
      allowed: result.allowed,
      remainingRequests: result.remainingAttempts,
      resetTime: result.lockoutUntil,
    };
  }

  /**
   * 🔒 共通の試行チェックロジック
   */
  private checkAttempt(
    identifier: string,
    method: 'login' | 'nfc' | 'general',
    maxAttempts: number,
    lockoutDuration: number
  ): {
    allowed: boolean;
    remainingAttempts?: number;
    lockoutUntil?: number;
    reason?: string;
  } {
    const now = Date.now();
    const storageKey = this.getStorageKey(method);
    const record = this.getAttemptRecord(storageKey, identifier);

    // ロックアウト期間チェック
    if (record.lockedUntil && now < record.lockedUntil) {
      return {
        allowed: false,
        lockoutUntil: record.lockedUntil,
        reason: `Locked until ${new Date(record.lockedUntil).toLocaleTimeString()}`,
      };
    }

    // ロックアウト期間が過ぎている場合はリセット
    if (record.lockedUntil && now >= record.lockedUntil) {
      this.resetAttempts(identifier, method);
      return {
        allowed: true,
        remainingAttempts: maxAttempts - 1,
      };
    }

    // 制限内かチェック
    if (record.count < maxAttempts) {
      return {
        allowed: true,
        remainingAttempts: maxAttempts - record.count - 1,
      };
    }

    // 制限を超えた場合はロックアウト
    return {
      allowed: false,
      lockoutUntil: now + lockoutDuration,
      reason: `Too many attempts. Try again later.`,
    };
  }

  /**
   * ✅ 成功時の処理（試行カウントリセット）
   */
  recordSuccess(identifier: string, method: 'login' | 'nfc' | 'general'): void {
    this.resetAttempts(identifier, method);
  }

  /**
   * ❌ 失敗時の処理（試行カウント増加）
   */
  recordFailure(identifier: string, method: 'login' | 'nfc' | 'general'): void {
    const now = Date.now();
    const storageKey = this.getStorageKey(method);
    const record = this.getAttemptRecord(storageKey, identifier);

    // 新しい試行記録を作成
    const updatedRecord: AttemptRecord = {
      ...record,
      count: record.count + 1,
      lastAttempt: now,
      firstAttempt: record.firstAttempt || now,
      ipAddress: this.getCurrentIP(),
      userAgent: navigator.userAgent,
    };

    // 制限を超えた場合はロックアウト時刻を設定
    const maxAttempts = this.getMaxAttempts(method);
    const lockoutDuration = this.getLockoutDuration(method);
    
    if (updatedRecord.count >= maxAttempts) {
      updatedRecord.lockedUntil = now + lockoutDuration;
    }

    this.saveAttemptRecord(storageKey, identifier, updatedRecord);
  }

  /**
   * 🧹 試行記録のリセット
   */
  resetAttempts(identifier: string, method: 'login' | 'nfc' | 'general'): void {
    const storageKey = this.getStorageKey(method);
    const attempts = this.getAllAttempts(storageKey);
    delete attempts[identifier];
    this.saveAllAttempts(storageKey, attempts);
  }

  /**
   * 🗂️ 現在の制限状況を取得（管理者用）
   */
  getCurrentStatus(): {
    loginAttempts: Record<string, AttemptRecord>;
    nfcAttempts: Record<string, AttemptRecord>;
    generalRequests: Record<string, AttemptRecord>;
  } {
    return {
      loginAttempts: this.getAllAttempts(STORAGE_KEYS.LOGIN_ATTEMPTS),
      nfcAttempts: this.getAllAttempts(STORAGE_KEYS.NFC_ATTEMPTS),
      generalRequests: this.getAllAttempts(STORAGE_KEYS.GENERAL_REQUESTS),
    };
  }

  /**
   * 🧽 古い記録のクリーンアップ
   */
  cleanup(): void {
    const now = Date.now();
    const maxAge = 24 * 60 * 60 * 1000; // 24時間

    Object.values(STORAGE_KEYS).forEach(storageKey => {
      const attempts = this.getAllAttempts(storageKey);
      const cleaned: Record<string, AttemptRecord> = {};

      Object.entries(attempts).forEach(([identifier, record]) => {
        // アクティブなロックアウトまたは24時間以内の記録は保持
        if (
          (record.lockedUntil && now < record.lockedUntil) ||
          (now - record.lastAttempt < maxAge)
        ) {
          cleaned[identifier] = record;
        }
      });

      this.saveAllAttempts(storageKey, cleaned);
    });
  }

  /**
   * 🔄 定期クリーンアップタイマー
   */
  private startCleanupTimer(): void {
    this.cleanupTimer = setInterval(() => {
      this.cleanup();
    }, SECURITY_CONFIG.CLEANUP_INTERVAL);
  }

  /**
   * 🛑 クリーンアップタイマー停止
   */
  stopCleanupTimer(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = null;
    }
  }

  // =======================================================================
  // 🔧 内部ユーティリティメソッド
  // =======================================================================

  private getStorageKey(method: 'login' | 'nfc' | 'general'): string {
    switch (method) {
      case 'login':
        return STORAGE_KEYS.LOGIN_ATTEMPTS;
      case 'nfc':
        return STORAGE_KEYS.NFC_ATTEMPTS;
      case 'general':
        return STORAGE_KEYS.GENERAL_REQUESTS;
      default:
        throw new Error(`Unknown method: ${method}`);
    }
  }

  private getMaxAttempts(method: 'login' | 'nfc' | 'general'): number {
    switch (method) {
      case 'login':
        return SECURITY_CONFIG.MAX_LOGIN_ATTEMPTS;
      case 'nfc':
        return SECURITY_CONFIG.MAX_NFC_ATTEMPTS;
      case 'general':
        return SECURITY_CONFIG.MAX_REQUESTS_PER_MINUTE;
      default:
        return SECURITY_CONFIG.MAX_LOGIN_ATTEMPTS;
    }
  }

  private getLockoutDuration(method: 'login' | 'nfc' | 'general'): number {
    switch (method) {
      case 'login':
        return SECURITY_CONFIG.LOCKOUT_DURATION;
      case 'nfc':
        return SECURITY_CONFIG.NFC_LOCKOUT_DURATION;
      case 'general':
        return 60 * 1000; // 1分間
      default:
        return SECURITY_CONFIG.LOCKOUT_DURATION;
    }
  }

  private getAttemptRecord(storageKey: string, identifier: string): AttemptRecord {
    const attempts = this.getAllAttempts(storageKey);
    return attempts[identifier] || {
      count: 0,
      firstAttempt: 0,
      lastAttempt: 0,
      method: storageKey.includes('login') ? 'login' : 
               storageKey.includes('nfc') ? 'nfc' : 'general',
    };
  }

  private getAllAttempts(storageKey: string): Record<string, AttemptRecord> {
    try {
      const stored = localStorage.getItem(storageKey);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.warn(`Failed to parse ${storageKey}:`, error);
      return {};
    }
  }

  private saveAttemptRecord(storageKey: string, identifier: string, record: AttemptRecord): void {
    const attempts = this.getAllAttempts(storageKey);
    attempts[identifier] = record;
    this.saveAllAttempts(storageKey, attempts);
  }

  private saveAllAttempts(storageKey: string, attempts: Record<string, AttemptRecord>): void {
    try {
      localStorage.setItem(storageKey, JSON.stringify(attempts));
    } catch (error) {
      console.error(`Failed to save ${storageKey}:`, error);
    }
  }

  private getCurrentIP(): string {
    // ブラウザからは直接IPアドレスを取得できないため、
    // サーバーから取得した値をセッションストレージに保存して使用
    return sessionStorage.getItem('client_ip') || 'unknown';
  }
}

// 🌐 シングルトンインスタンス
export const rateLimiter = new RateLimiter();

// 🔧 ユーティリティ関数
export const checkLoginAttempt = (email: string) => rateLimiter.checkLoginAttempt(email);
export const checkNFCAttempt = (nfcUid: string) => rateLimiter.checkNFCAttempt(nfcUid);
export const checkGeneralRequest = (identifier?: string) => rateLimiter.checkGeneralRequest(identifier);

export const recordLoginSuccess = (email: string) => rateLimiter.recordSuccess(email, 'login');
export const recordLoginFailure = (email: string) => rateLimiter.recordFailure(email, 'login');

export const recordNFCSuccess = (nfcUid: string) => rateLimiter.recordSuccess(nfcUid, 'nfc');
export const recordNFCFailure = (nfcUid: string) => rateLimiter.recordFailure(nfcUid, 'nfc');

// 🧹 クリーンアップ
export const cleanupRateLimit = () => rateLimiter.cleanup();

// 📊 管理者用ステータス
export const getRateLimitStatus = () => rateLimiter.getCurrentStatus();

// Default export
export default rateLimiter;